import { ErrorMessage } from '@tunasong/tunasong-ui'
import { createBrowserRouter, SuspenseBusy } from '@tunasong/ui-lib'
import AppContainer from './app-container.js'
import {
  LazyEmbed,
  LazyErrorBoundary,
  LazyIndexPage,
  LazyLabs,
  LazyLogin,
  LazyLogout,
  LazyPage,
  LazyTermsOfService,
  LazyWaitlistPage,
  LazyRootMain,
  LazyProfile,
  LazyAudioMixer,
  LazyFavoritiesPage,
  LazyMusicTheoryPage,
  LazyModeLabPage,
  LazyFunctionQuizPage,
  LazyReharmLabPage,
  LazyCadenceLabPage,
  LazyMelodyWriterPage,
  LazyEarTrainingPage,
  LazyGuitarPatternsPage,
  LazyGuitarChordsPage,
  LazyExternalToolPage,
  LazyGamesPage,
  LazyRouterAppChrome,
  LazyRouterAppGroupChrome,
} from './lazy-routes.js'
import { pageChrome } from './page-chrome.js'
import { docsRouter } from './docs/docs-routes.js'

/**
 * @todo check auth https://www.robinwieruch.de/react-router-authentication/
 * @todo we can use the route loader to load the entities
 * @todo implement basename
 * */

const errorElement = <LazyErrorBoundary />

const router = createBrowserRouter(
  [
    { path: '', element: <LazyIndexPage />, index: true },

    {
      path: 'error',
      children: [
        {
          path: '404',
          element: <ErrorMessage message="Page is not found" />,
        },
      ],
    },
    {
      path: 'games/caged',
      element: (
        <AppContainer
          component={LazyGamesPage}
          audio={true}
          auth={false}
          theme="dark"
        />
      ),
    },

    { path: 'terms', element: <LazyTermsOfService /> },
    { path: 'waitlist', element: <LazyWaitlistPage /> },
    /** Public app paths */
    {
      path: 'embed',
      element: (
        <AppContainer
          component={LazyEmbed}
          audio={true}
          theme="light"
        />
      ),
    },
    {
      path: 'page/:slug',
      element: <AppContainer component={LazyPage} />,
    },

    /** Main app below */
    {
      path: 'app/',
      errorElement,
      children: [
        {
          path: '',
          index: true,
          element: (
            <AppContainer
              component={pageChrome(LazyRootMain, {
                chromeProps: {
                  pageTitle: 'Dashboard',
                },
              })}
              audio={true}

            />
          ),
        },
        {
          path: 'login',
          element: (
            <AppContainer component={LazyLogin} auth={false} />
          ),
        },
        {
          path: 'logout',
          element: (
            <AppContainer component={LazyLogout} auth={false} />
          ),
        },
        {
          path: 'e/:entityId/*',
          element: (
            <AppContainer
              component={LazyRouterAppChrome}
              audio={true}
            />
          ),
          HydrateFallback: SuspenseBusy,
        },
        {
          // group - still in development for groups / tabs
          path: 'g/:entityId/*',
          element: (
            <AppContainer
              component={LazyRouterAppGroupChrome}
              audio={true}
            />
          ),
          HydrateFallback: SuspenseBusy,
        },
        {
          path: 'mixer',
          element: (
            <AppContainer
              component={pageChrome(LazyAudioMixer, {
                container: true,
                containerProps: {
                  style: { paddingTop: 16 },
                },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'profile',
          element: (
            <AppContainer
              component={pageChrome(LazyProfile, { container: true })}
              audio={true}

            />
          ),
        },
        {
          path: 'favorites',
          element: (
            <AppContainer
              component={pageChrome(LazyFavoritiesPage, { container: true })}
              audio={true}

            />
          ),
        },

        {
          path: 'labs',
          element: (
            <AppContainer
              component={pageChrome(LazyLabs, {
                container: false,
                chromeProps: { pageTitle: 'Labs', pageIconName: 'Labs' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'theory',
          element: (
            <AppContainer
              component={pageChrome(LazyMusicTheoryPage, {
                container: false,
                chromeProps: { pageTitle: 'Music Theory', pageIconName: 'MusicNote' },
              })}
              audio={true}

            />
          ),
        },
        {
          path: 'modes',
          element: (
            <AppContainer
              component={pageChrome(LazyModeLabPage, {
                container: false,
                chromeProps: { pageTitle: 'Mode Lab', pageIconName: 'LibraryMusic' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'quiz',
          element: (
            <AppContainer
              component={pageChrome(LazyFunctionQuizPage, {
                container: false,
                chromeProps: { pageTitle: 'Function Quiz', pageIconName: 'Brain' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'reharm',
          element: (
            <AppContainer
              component={pageChrome(LazyReharmLabPage, {
                container: false,
                chromeProps: { pageTitle: 'Reharm Lab', pageIconName: 'Transform' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'cadences',
          element: (
            <AppContainer
              component={pageChrome(LazyCadenceLabPage, {
                container: false,
                chromeProps: { pageTitle: 'Cadence Lab', pageIconName: 'Functions' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'melody',
          element: (
            <AppContainer
              component={pageChrome(LazyMelodyWriterPage, {
                container: false,
                chromeProps: { pageTitle: 'Melody Writer', pageIconName: 'Compose' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'ear-training',
          element: (
            <AppContainer
              component={pageChrome(LazyEarTrainingPage, {
                container: false,
                chromeProps: { pageTitle: 'Ear Training', pageIconName: 'Headset' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'guitarchords',
          element: (
            <AppContainer
              component={pageChrome(LazyGuitarChordsPage, {
                chromeProps: { pageTitle: 'Guitar Chords', pageIconName: 'ElectricGuitar' },
                container: true,
              })}
              audio={true}

            />
          ),
        },
        {
          path: 'guitarpatterns',
          element: (
            <AppContainer
              component={pageChrome(LazyGuitarPatternsPage, {
                container: true,
                chromeProps: {
                  showPageHeader: true,
                  pageTitle: 'Guitar Patterns',
                  pageDescription: 'Guitar patterns and shapes',
                  pageIconName: 'Fretboard',
                },
              })}
              audio={true}

            />
          ),
        },

        {
          path: 'external-tool',
          element: (
            <AppContainer
              component={pageChrome(LazyExternalToolPage, {
                container: false,
                chromeProps: { pageTitle: 'External Tool', pageIconName: 'OpenLink' },
              })}

            />
          ),
        },

        // @deprecated - don't use below
        {
          path: ':entityType/:entityId/*',
          element: (
            <AppContainer
              component={LazyRouterAppChrome}
              audio={true}
            />
          ),
        },
        {
          path: 'entities/:entityId/*',
          element: (
            <AppContainer
              component={LazyRouterAppChrome}
              audio={true}
            />
          ),
        },
      ],
    },

    /** The docs router */
    ...docsRouter,

    { path: '*', element: <ErrorMessage message="Page is not found" /> },
  ],
  {
    /** So we can omit all /app/* in Links */
    // basename: '/app',
  }
)

export default router
